/**
 * The user web app's QueryClient.
 *
 * Built here rather than inline in the entry point so the caching decisions this
 * product depends on are testable, and so a screen cannot quietly disagree with
 * them.
 *
 * The retry policy comes from @anthra/platform/errors and is not restated: a 401,
 * a 403 and a 422 will not become a 200 on the third attempt, a NetworkError very
 * well might, and having that judgement in one place is what keeps the three
 * clients behaving alike (US-00-002.27).
 */

import { retryDelay, shouldRetry } from "@anthra/platform/errors"
import { QueryClient } from "@tanstack/react-query"

export function createQueryClient(): QueryClient {
	return new QueryClient({
		defaultOptions: {
			queries: {
				retry: shouldRetry,
				retryDelay,
				// A browser tab is left open for hours. US-00-002.8 requires the
				// lists to refetch on focus, and a stale window is the only way a
				// user sees an expert reply without pressing anything.
				refetchOnWindowFocus: true,
				staleTime: 30_000,
			},
			mutations: {
				// NEVER retried automatically. Every write in this product carries
				// an idempotency key, but the key only makes a DELIBERATE retry
				// safe — an automatic one would also re-send a request the user
				// abandoned, and on a create-query that is a duplicate submission
				// they never asked for (US-00-002.27).
				retry: 0,
				// ATTEMPT THE WRITE EVEN WHEN THE BROWSER REPORTS ITSELF OFFLINE.
				//
				// TanStack's default, `networkMode: "online"`, PAUSES a mutation
				// while navigator.onLine is false: it never runs, so it never
				// settles, so `isError` never becomes true. Submitting a query with
				// the connection down therefore sat on "Sending your query…"
				// indefinitely — measured at 93 seconds and still going — with no
				// error, no retry and the Send button disabled, which is the one
				// outcome US-00-002.27 rules out (TC-US-00-002-10-3).
				//
				// "always" lets the request run and fail like any other, so the
				// screen's existing error panel and its retry are reached by the
				// ordinary path rather than needing an offline branch of their own.
				// Paired with `retry: 0` above, a failure is shown once and the
				// resend stays the user's decision.
				networkMode: "always",
			},
		},
	})
}
